import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Logo } from "../components/Logo";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { Footer } from "../components/Footer";
import { useI18n } from "../i18n/I18nContext";
import { api, formatApiError } from "../lib/api";
import { ShieldCheck, Mail, Lock, ArrowRight } from "lucide-react";

const Login = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      const target =
        data.stage === "mfa_setup_required" ? "/mfa/setup" : "/mfa/verify";
      navigate(target, { state: { ...data, email } });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white grid-bg flex flex-col">
      <header className="border-b border-white/[0.08]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/" data-testid="login-home-link"><Logo size={28} /></Link>
          <LanguageSwitcher />
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="card-sharp p-8 lg:p-10" data-testid="login-card">
            <div className="flex items-center gap-2.5 text-[#19A7E0]">
              <ShieldCheck className="w-4 h-4" />
              <p className="tactical-label">{t("auth.loginSubtitle")}</p>
            </div>
            <h1 className="mt-4 text-3xl tracking-tight font-medium">{t("auth.loginTitle")}</h1>

            <form className="mt-8 space-y-5" onSubmit={onSubmit} data-testid="login-form">
              <div>
                <label className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/50">
                  {t("auth.email")}
                </label>
                <div className="mt-2 relative">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="login-email-input"
                    className="w-full bg-[#0A0D14] border border-white/15 focus:border-[#19A7E0] focus:ring-1 focus:ring-[#19A7E0] rounded-sm pl-10 pr-3 py-3 text-sm outline-none"
                    placeholder="cliente@instalros.co"
                  />
                </div>
              </div>

              <div>
                <label className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/50">
                  {t("auth.password")}
                </label>
                <div className="mt-2 relative">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="login-password-input"
                    className="w-full bg-[#0A0D14] border border-white/15 focus:border-[#19A7E0] focus:ring-1 focus:ring-[#19A7E0] rounded-sm pl-10 pr-3 py-3 text-sm outline-none font-mono tracking-wider"
                    placeholder="••••••••••"
                  />
                </div>
              </div>

              {error && (
                <p
                  className="text-xs text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-sm px-3 py-2"
                  data-testid="login-error"
                >
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="login-submit-btn"
                className="w-full inline-flex items-center justify-center gap-2 btn-primary py-3 text-xs font-mono uppercase tracking-[0.18em] rounded-sm disabled:opacity-50"
              >
                {loading ? "…" : t("auth.signIn")} <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          </div>

          <p className="mt-6 text-center text-[10px] font-mono uppercase tracking-[0.25em] text-white/30">
            © 2026 IN|STALROS Intelligence &amp; Integration
          </p>
        </div>
      </main>
    </div>
  );
};

export default Login;
